import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import {
  MdCheck,
  MdChevronLeft,
  MdChevronRight,
  MdSwapVert,
} from 'react-icons/md';
import { Popover } from 'react-tiny-popover';
import { useDebounce, useLocalStorage } from 'react-use';
import { ChatInput } from '@/features/ai-agents/components/chat-input';

// @ts-ignore
import AddImage from '@/features/editor/widgets/shared/ai-editing-layout/widget-no-image.png';
import { STARTER_USE_CASES } from '@/features/editor/widgets/ai-widget-builder/use-cases/starter-use-cases';
import { VSpace } from '@/components/spacing';
import { useCopilotStartersApi } from '@/features/copilot-starters/api';

// @ts-ignore
import MornIcon from './morn.png';
// @ts-ignore
import NoonIcon from './noon.png';
// @ts-ignore
import NightIcon from './night.png';
import { useAiMetadataApi } from '@/features/ai-agents/shared/use-ai-metadata-api';
import { useFileDropDetector } from '@/features/ai-agents/shared/use-file-drop-detector';
import {
  getQueryParam,
  isBrowser,
  nav,
  useDebouncedWindowDims,
} from '@/utils/browser';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import {
  AccountContext,
  getCoPilotModeConfig,
  useFeatureBit,
} from '@/features/account-context';
import { MyExperiencesContext } from '@/features/dashboard/experiences/context';
import { ExperienceFilter } from '@/webapi/use-experience-api';
import { breakpoints, globalBreakpointsW } from '@/components/responsive';
import { Role } from '@/utils/definitions';
import Portal from '@/components/portal';
import { FeatureBit } from '@/webapi/use-auth-api';
import { TabSelect } from '@/components/tab-select';
import {
  ANALYZE_AGENT_ID,
  CopilotMode,
  useCopilotMode,
} from '@/features/dashboard/experiences/use-copilot-mode';
import { editorRoute } from '@/webapi/pages';

export const COPILOT_MODE_KEY = `vsly:starters-expanded`;

interface ExperienceStarter {
  title: string;
  image: string;
  prompt: string;
  tags: string[];
  attachments?: string[];
  isPublished: boolean;
}

const FALLBACK_STARTERS: ExperienceStarter[] = STARTER_USE_CASES.map((sc) => ({
  title: sc.title,
  image: sc.image,
  prompt: sc.prompt || ``,
  tags: sc.tags,
  attachments: [],
  isPublished: true,
}));

const copilotStarterToExperienceStarter = (s: {
  title: string;
  coverImage: string;
  attachments: string[];
  prompt: string;
  group: string;
  isPublished: boolean;
}): ExperienceStarter => ({
  title: s.title,
  image: s.coverImage || s.attachments?.[0] || ``,
  prompt: s.prompt,
  tags: [`All`, ...(s.group ? [s.group] : [])],
  isPublished: s.isPublished,
  attachments: s.attachments,
});

const isMacPlatform = () =>
  isBrowser() && navigator.platform.toUpperCase().includes(`MAC`);

export interface FullExperienceAiBuilderProps {
  liveExperiencesCount: number;
  draftExperiencesCount: number;
  position: 'top' | 'bottom';
  onPositionChange: (position: 'top' | 'bottom') => void;
  onAfterSubmit?: (mode: CopilotMode) => void;
  showGreeting?: boolean;
  forcedDefaultCopilotMode?: CopilotMode;
}

export function FullExperienceAiBuilder({
  liveExperiencesCount,
  draftExperiencesCount,
  position,
  onPositionChange,
  onAfterSubmit,
  showGreeting = true,
  forcedDefaultCopilotMode,
}: FullExperienceAiBuilderProps) {
  const { width } = useDebouncedWindowDims();
  const isMobile = width < globalBreakpointsW.md;

  const { account, trialInfo } = useContext(AccountContext);
  const agentId = account?.store?.defaultAiModels?.[`cro-agent`] || `cro-agent`;
  const isSystemUser = account?.store?.role === Role.SYSTEM;
  const {
    refreshList,
    filter,
    setFilter,
    loading: isExpLoading,
  } = React.useContext(MyExperiencesContext);
  const modeCfg = getCoPilotModeConfig();
  const [activeTag, setActiveTag] = useState(`All`);
  const [isLoading, setIsLoading] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const { copilotMode, selectCopilotMode } = useCopilotMode(
    forcedDefaultCopilotMode,
  );
  const [displayedCopilotMode, setDisplayedCopilotMode] = useState<CopilotMode>(
    copilotMode || `Build`,
  );
  const [isPlaceholderVisible, setIsPlaceholderVisible] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [starters, setStarters] =
    useState<ExperienceStarter[]>(FALLBACK_STARTERS);

  const enableAgentSwitching = useFeatureBit(FeatureBit.SHOW_AI_MODEL_SWITCHER);
  const enableConversationTypeSelection =
    useFeatureBit(FeatureBit.ENABLE_PINS, false) && modeCfg?.tabs?.length > 1;

  const { listStarters } = useCopilotStartersApi();

  useEffect(() => {
    listStarters(copilotMode).then((res) => {
      const all = (res?.starters || []).map(copilotStarterToExperienceStarter);
      const visible = isSystemUser ? all : all.filter((s) => s.isPublished);
      const fallback = copilotMode === `Build` ? FALLBACK_STARTERS : [];
      setStarters(visible.length > 0 ? visible : fallback);
    });
    setActiveTag(`All`);
  }, [isSystemUser, copilotMode]);

  const [isStartersExpanded, setIsStartersExpanded] = useLocalStorage(
    COPILOT_MODE_KEY,
    false,
  );
  const startersHasSet = useRef(
    isBrowser() && localStorage.getItem(COPILOT_MODE_KEY) !== null,
  );

  useDebounce(
    () => {
      if (!isExpLoading && startersHasSet.current === false) {
        setIsStartersExpanded(
          liveExperiencesCount <= 2 && draftExperiencesCount <= 6,
        );
        startersHasSet.current = true;
      }
    },
    1000,
    [isExpLoading, liveExperiencesCount, draftExperiencesCount],
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const desktopPlaceholder = `Hi, I’m Visually Copilot. I can build, edit, test and personalize any part of your store based your visitors experience\nWhat would you like to do today?`;
  const analyzeDesktopPlaceholder = `What do you want to uncover about your customer experience?\nExplore behavior, friction, conversion drivers, and hidden opportunities across your store.`;
  const mobilePlaceholder = `Hi, I’m Visually Copilot.\nWhat would you like to do today?`;
  const selectedAgentId =
    copilotMode === `Analyze` ? ANALYZE_AGENT_ID : agentId;
  let chatPlaceholder = desktopPlaceholder;
  if (displayedCopilotMode === `Analyze`)
    chatPlaceholder = analyzeDesktopPlaceholder;
  else if (isMobile) chatPlaceholder = mobilePlaceholder;

  useEffect(() => {
    if (copilotMode === displayedCopilotMode) {
      setIsPlaceholderVisible(true);
      return undefined;
    }

    setIsPlaceholderVisible(false);
    const transitionTimer = setTimeout(() => {
      setDisplayedCopilotMode(copilotMode);
      setIsPlaceholderVisible(true);
    }, 250);

    return () => clearTimeout(transitionTimer);
  }, [copilotMode, displayedCopilotMode]);

  const { runExperienceEditor } = useAiMetadataApi(
    getQueryParam(`local-ai`) === `1`,
  );

  const dropzoneVisible = useFileDropDetector();

  const tags = [
    `All`,
    ...Array.from(
      new Set(starters.flatMap((s) => s.tags.filter((t) => t !== `All`))),
    ),
  ];

  const filteredStarters =
    activeTag === `All`
      ? starters
      : starters.filter((s) => s.tags.includes(activeTag));

  const checkScrollAvailability = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(checkScrollAvailability, 0);
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener(`scroll`, checkScrollAvailability);
      window.addEventListener(`resize`, checkScrollAvailability);
    }
    return () => {
      clearTimeout(timer);
      if (scrollElement) {
        scrollElement.removeEventListener(`scroll`, checkScrollAvailability);
      }
      window.removeEventListener(`resize`, checkScrollAvailability);
    };
  }, [filteredStarters, isStartersExpanded, activeTag]);

  const onTabSelected = (name: string) => {
    setActiveTag(name);
  };

  const scrollToExperienceList = () => {
    document
      .querySelector(`#experience-list .experience-heading`)
      ?.scrollIntoView({ behavior: `smooth`, block: `start` });
  };

  const onSubmit = async (msg: AiChatMessage) => {
    setIsLoading(true);
    await runExperienceEditor(
      msg,
      account?.store?.alias,
      copilotMode === `Analyze` ? ANALYZE_AGENT_ID : undefined,
    );
    // eslint-disable-next-line no-promise-executor-return
    await new Promise((resolve) => setTimeout(resolve, 1500));
    if (onAfterSubmit) {
      onAfterSubmit(copilotMode);
    } else if (
      !!filter &&
      filter !== ExperienceFilter.DRAFT &&
      filter !== ExperienceFilter.AI_ALL_FILTER
    ) {
      setFilter(ExperienceFilter.AI_ALL_FILTER);
    } else {
      refreshList(true);
    }

    if (isStartersExpanded) {
      scrollToExperienceList();
    }

    setIsLoading(false);
  };

  const onToggleStarters = () => {
    setIsStartersExpanded(!isStartersExpanded);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = direction === `left` ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: `smooth` });
    }
  };

  const getTimeDetails = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
      return { text: `Good morning`, icon: MornIcon };
    }
    if (hour >= 12 && hour < 18) {
      return { text: `Good afternoon`, icon: NoonIcon };
    }
    return { text: `Good evening`, icon: NightIcon };
  };

  const { text, icon } = getTimeDetails();

  const togglePosition = () => {
    onPositionChange(position === `top` ? `bottom` : `top`);
    setIsPopoverOpen(false);
  };

  const sendStarter = (starter: ExperienceStarter) => {
    if (copilotMode === `Analyze`) {
      onSubmit({ content: starter.prompt });
      return;
    }

    const message = `
${starter.prompt}
Use the attached images as reference only. Adapt the design to match the store’s style, and draw from the existing components so it follows the same design language and principles.`;
    onSubmit({
      content: message,
      images: [
        ...(starter?.attachments || []).map((s) => ({ url: s })),
        { url: starter.image },
      ],
    });
  };

  return (
    <Wrapper
      id="full-experience-ai-builder-wrapper"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <VSpace value={1} className="only-desktop top-margin" />
      {showGreeting && (
        <Popover
          isOpen={isPopoverOpen}
          positions={[`bottom`, `top`]}
          onClickOutside={() => setIsPopoverOpen(false)}
          content={
            <PopoverContent onClick={togglePosition}>
              <MdSwapVert size={18} />
              {position === `top`
                ? `Move below experiences`
                : `Move above experiences`}
            </PopoverContent>
          }
        >
          <GreetingWrapper onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
            <GreetingIcon src={icon} alt="" />
            <h1>
              {text},{` `}
              {trialInfo?.daysRemaining > 0 &&
                !isMobile &&
                `Welcome to Visually.`}
              {isMobile ? `\n` : ` `}
              What would you like to do?
            </h1>
          </GreetingWrapper>
        </Popover>
      )}
      <Container id="full-experience-ai-builder">
        <BetaBadge>BETA</BetaBadge>
        <RainbowBg
          isVisible={isLoading}
          isFocused={isFocused}
          isHovered={isHovered}
        />
        <AnimatePresence>
          {isLoading && (
            <Overlay
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <CheckIconWrapper
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  delay: 0.2,
                  type: `spring`,
                  stiffness: 260,
                  damping: 20,
                }}
              >
                <MdCheck size={32} color="#fff" />
              </CheckIconWrapper>
            </Overlay>
          )}
        </AnimatePresence>
        <HeaderSection $isBlurred={isLoading}>
          {enableConversationTypeSelection && (
            <AgentSelectionWrapper>
              <TabSelect
                tabs={modeCfg.tabs}
                defaultValue={copilotMode}
                onTabSelected={async (_idx, name) => {
                  if (modeCfg.buildShouldOpenEditor && name === `Build`) {
                    await nav(editorRoute());
                  } else {
                    selectCopilotMode(name as CopilotMode);
                  }
                }}
                tabFont="Inter"
                tabFontSize="1.1rem"
                tabWidth={9}
                tabHeight={3}
                tabRadius={1.6}
                tabPadding="0"
                containerPadding={0}
                disabledColor="#4a5968"
              />
            </AgentSelectionWrapper>
          )}

          <ChatWrapper
            $isPlaceholderVisible={isPlaceholderVisible}
            onKeyDown={() => setHasTyped(true)}
          >
            <ChatInput
              showSendText
              showDropZone={dropzoneVisible}
              minLines={2}
              onSubmit={onSubmit}
              placeholder={chatPlaceholder}
              enableFilesUpload
              enableWhiteboard={false}
              enableExamples={false}
              enableVoiceInput={false}
              enableCustomToolButton
              customToolButtonText={
                isStartersExpanded ? `Hide Starters` : `Show Starters`
              }
              selectedAgent={{ id: selectedAgentId, type: `agent` }}
              enableAgentSelection={enableAgentSwitching}
              hideAgentSelection={copilotMode === `Analyze`}
              onCustomToolButtonClicked={onToggleStarters}
              cmdEnterToSend
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                setHasTyped(false);
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
            <Portal selector="#ai-chat-input-tools-wrapper .right > div:last-of-type">
              <SendHint $visible={hasTyped}>
                {isMacPlatform() ? `⌘` : `Ctrl`}
                {` + Enter to send`}
              </SendHint>
            </Portal>
          </ChatWrapper>
        </HeaderSection>

        <AnimatePresence initial={false}>
          {isStartersExpanded && filteredStarters.length > 0 && (
            <StartersContent
              $isBlurred={isLoading}
              initial={{ height: 0, opacity: 0, y: -12 }}
              animate={{ height: `auto`, opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -12 }}
              transition={{ duration: 0.25, ease: `easeInOut` }}
            >
              <VSpace value={3} />
              <Divider />
              <VSpace value={2} />
              <StartersSection>
                <StartersHeader>
                  <StartersTitle>Starters</StartersTitle>
                  <TabsContainer>
                    <TabsScrollWrapper>
                      {tags.map((tag) => (
                        <Tab
                          key={tag}
                          active={activeTag === tag}
                          onClick={() => onTabSelected(tag)}
                        >
                          {tag}
                        </Tab>
                      ))}
                    </TabsScrollWrapper>
                  </TabsContainer>
                </StartersHeader>

                {copilotMode === `Build` && (
                  <CardsWrapper>
                    {canScrollLeft && (
                      <ScrollButton
                        direction="left"
                        onClick={() => scroll(`left`)}
                      >
                        <MdChevronLeft size={24} />
                      </ScrollButton>
                    )}
                    <CardsContainer ref={scrollRef}>
                      {filteredStarters.map((starter, idx) => (
                        <StarterCard
                          key={idx}
                          $isDraft={!starter.isPublished}
                          onClick={() => sendStarter(starter)}
                        >
                          {!starter.isPublished && (
                            <DraftBadge>Draft</DraftBadge>
                          )}
                          <CardImage
                            src={starter.image || AddImage}
                            alt={starter.title}
                          />
                          <CardTitle>{starter.title}</CardTitle>
                        </StarterCard>
                      ))}
                    </CardsContainer>
                    {canScrollRight && (
                      <ScrollButton
                        direction="right"
                        onClick={() => scroll(`right`)}
                      >
                        <MdChevronRight size={24} />
                      </ScrollButton>
                    )}
                  </CardsWrapper>
                )}

                {copilotMode === `Analyze` && (
                  <TilesScrollWrapper>
                    <TilesGrid>
                      {filteredStarters.map((starter, idx) => (
                        <TextTile
                          key={idx}
                          $isDraft={!starter.isPublished}
                          onClick={() => sendStarter(starter)}
                        >
                          {!starter.isPublished && (
                            <TileDraftBadge>Draft</TileDraftBadge>
                          )}
                          <TileTitle>{starter.title}</TileTitle>
                          <TileChevron>
                            <MdChevronRight size={18} />
                          </TileChevron>
                        </TextTile>
                      ))}
                    </TilesGrid>
                  </TilesScrollWrapper>
                )}
              </StartersSection>
            </StartersContent>
          )}
        </AnimatePresence>
      </Container>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  gap: 3rem;
  z-index: 0;

  textarea {
    font-family: Inter, sans-serif !important;
  }
  textarea::placeholder {
    font-family: Inter, sans-serif;
  }
`;

const RainbowBg = styled.div<{
  isVisible: boolean;
  isFocused: boolean;
  isHovered: boolean;
}>`
  position: absolute;
  inset: 0.2rem;
  border-radius: 2.4rem;
  filter: blur(8px);
  background: linear-gradient(
    110deg,
    #bf85ff,
    #f091e7,
    #ffb0d8,
    #ffcecc,
    #bf85ff,
    #f091e7,
    #ffb0d8,
    #ffcecc,
    #bf85ff,
    #f091e7,
    #ffb0d8,
    #ffcecc,
    #bf85ff,
    #f091e7,
    #ffb0d8,
    #ffcecc
  );
  background-size: 400% 400%;
  animation: rainbow 18s linear infinite;
  z-index: -1;
  opacity: ${(props) => {
    if (props.isVisible) return 1;
    if (props.isFocused || props.isHovered) return 0.5;
    return 0.2;
  }}!important;
  transition: opacity 0.5s ease-in-out;

  @keyframes rainbow {
    0% {
      background-position: 0 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 1% 50%;
    }
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #f1f3f5;
`;

const Overlay = styled(motion.div)`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(8px);
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2rem;
`;

const CheckIconWrapper = styled(motion.div)`
  width: 6rem;
  height: 6rem;
  background: #000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const GreetingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  align-self: center;
  cursor: move;
  flex-direction: row;

  ${breakpoints.down(`md`)} {
    flex-direction: column;
  }

  h1 {
    font-family: 'Eesti', serif;
    font-size: 2.4rem;
    font-weight: bold;
    color: #000;
    margin: 0;
    white-space: pre-line;
  }
`;

const GreetingIcon = styled.img`
  width: 4rem;
  height: 4rem;
  object-fit: contain;
  filter: contrast(0.95);
`;

const Container = styled.div`
  position: relative;
  background: white;
  border-radius: 2rem;
  padding: 2.2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
  margin-bottom: 2.4rem;
  border: 1px solid #e2e5e9;
  width: 100%;

  ${breakpoints.down(`md`)} {
    padding: 2rem;
    margin-bottom: 0;
  }
`;

const HeaderSection = styled.div<{ $isBlurred: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  filter: ${(props) => (props.$isBlurred ? `blur(4px)` : `none`)};
  transition: filter 0.3s ease;
`;

const AgentSelectionWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: -0.6rem;
`;

const StartersContent = styled(motion.div)<{ $isBlurred: boolean }>`
  overflow: hidden;
  filter: ${(props) => (props.$isBlurred ? `blur(4px)` : `none`)};
  transition: filter 0.3s ease;
`;

const ChatWrapper = styled.div<{ $isPlaceholderVisible: boolean }>`
  position: relative;
  .ai-chat-input-wrapper {
    border: none;
    box-shadow: none;
    border-radius: 0;
    padding: 0;

    textarea {
      line-height: 1.5;
      align-self: flex-start;
      width: 100%;
      min-width: 0;
      max-width: 100%;
      font-size: 1.6rem;
      font-weight: 400;
      white-space: pre-wrap;
      overflow-wrap: normal;
      word-break: normal;
      text-wrap: pretty;
      ::placeholder {
        color: #414141;
        opacity: ${(props) => (props.$isPlaceholderVisible ? 1 : 0)};
        transition: opacity 0.16s ease-in-out;
      }
    }

    ${breakpoints.down(`md`)} {
      gap: 2rem;

      #ai-chat-input-tools-wrapper {
        grid-template-columns: 1fr auto;
      }

      textarea {
        max-width: 100%;
      }
    }
  }
`;

const SendHint = styled.div<{ $visible: boolean }>`
  display: flex;
  align-items: center;
  font-family: Eesti, sans-serif;
  font-size: 1.2rem;
  letter-spacing: -0.2px;
  color: #b0b8c1;
  user-select: none;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.2s ease;

  ${breakpoints.down(`md`)} {
    display: none;
  }
`;

const StartersSection = styled.div``;

const StartersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4rem;
  margin-bottom: 2.4rem;
`;

const StartersTitle = styled.h3`
  font-family: 'Inter', serif;
  font-size: 1.8rem;
  font-weight: 600;
  margin: 0;
`;

const TabsContainer = styled.div`
  position: relative;
  overflow: hidden;
  flex: 1;

  ${breakpoints.down(`md`)} {
    mask-image: linear-gradient(
      to right,
      transparent,
      black 5%,
      black 95%,
      transparent
    );
  }
`;

const TabsScrollWrapper = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  flex-shrink: 0;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 3rem;
  background-color: ${(props) => (props.active ? `#000` : `transparent`)};
  color: ${(props) => (props.active ? `#fff` : `#666`)};
  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'Inter', serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => (props.active ? `#000` : `#e8e8e8`)};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const CardsWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin: 0 -3rem;

  ${breakpoints.down(`md`)} {
    margin: 0 -2rem;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  gap: 2.5rem;
  overflow-x: auto;
  padding: 0.4rem 3rem;
  scroll-behavior: smooth;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  width: 100%;

  mask-image: linear-gradient(
    to right,
    transparent,
    black 5%,
    black 95%,
    transparent
  );
`;

const ScrollButton = styled.button<{ direction: 'left' | 'right' }>`
  background: white;
  border: 1px solid #e2e5e9;
  border-radius: 50%;
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  ${(props) =>
    props.direction === `left` ? `left: 1.5rem;` : `right: 1.5rem;`}
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  ${breakpoints.down(`md`)} {
    ${(props) =>
      props.direction === `left` ? `left: 0.5rem;` : `right: 0.5rem;`}
  }

  &:hover {
    background: #f8f9fb;
  }
`;

const StarterCard = styled.div<{ $isDraft?: boolean }>`
  flex: 0 0 15rem;
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
  opacity: ${({ $isDraft }) => ($isDraft ? 0.7 : 1)};

  &:hover {
    transform: translateY(-4px);
  }
`;

const DraftBadge = styled.span`
  position: absolute;
  top: 0.6rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  font-family: Inter, sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: white;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  padding: 0.2rem 0.7rem;
  border-radius: 2rem;
  white-space: nowrap;
  pointer-events: none;
`;

const TileDraftBadge = styled.span`
  position: absolute;
  top: -0.7rem;
  left: 1.4rem;
  z-index: 2;
  font-family: Inter, sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  padding: 0.2rem 0.7rem;
  border-radius: 2rem;
  white-space: nowrap;
  pointer-events: none;
`;

const CardImage = styled.img`
  width: 100%;
  height: 26rem;
  object-fit: cover;
  border-radius: 1.6rem;
  border: 1px solid #e2e5e9;
  margin-bottom: 1.2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
`;

const CardTitle = styled.div`
  font-size: 1.2rem;
  font-family: Inter, sans-serif;
  font-weight: 300;
  color: #414141;
  text-align: start;
`;

const TilesScrollWrapper = styled.div`
  max-height: 37rem;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 0.4rem 0.2rem;
  padding-bottom: 4rem;
  margin: -0.4rem -0.2rem;
  mask-image: linear-gradient(to bottom, black 0%, black 85%, transparent 100%);

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.6rem;

  ${breakpoints.down(`lg`)} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${breakpoints.down(`sm`)} {
    grid-template-columns: 1fr;
  }
`;

const TextTile = styled.div<{ $isDraft?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2rem;
  padding: 3.5rem;
  border-radius: 1rem;
  border: 1px solid #e6e8eb;
  background: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04), 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  cursor: pointer;
  opacity: ${({ $isDraft }) => ($isDraft ? 0.7 : 1)};
  transition: all 0.2s ease;

  &:hover {
    border-color: #d5d9dc;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const TileTitle = styled.div`
  font-size: 1.4rem;
  font-family: Inter, sans-serif;
  font-weight: 400;
  color: #292929;
  line-height: 1.4;
  text-align: start;
`;

const TileChevron = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #a0aab4;
`;

const PopoverContent = styled.div`
  background: white;
  border: 1px solid #e2e5e9;
  border-radius: 0.8rem;
  padding: 1rem 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: 'Inter', sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #000;
  white-space: nowrap;
  user-select: none;

  &:hover {
    background: #f8f9fb;
  }
`;

const BetaBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 1;
  background-color: rgb(244, 245, 247);
  color: rgb(200 200 200);
  padding: 0.5rem 0.8rem;
  border-radius: 20rem;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: Inter, serif;
  width: auto;

  ${breakpoints.down(`md`)} {
    top: 0.7rem;
    right: 0.7rem;
  }
`;
